import {
  MapPin,
  MonitorSmartphone,
  Clapperboard,
  Megaphone,
  Network,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  AD_FORMAT_LABELS,
  Gender,
  RequestSource,
  TRAFFIC_TYPE_LABELS,
} from "@adlywise/shared";
import type { LookupKind } from "@/features/analytics/lookupApi";
import type { AnalyticsSource, AnalyticsFieldKey } from "@adlywise/shared";
import {
  COUNTRY_OPTIONS,
  DEVICE_OPTIONS,
  KIND_OPTIONS,
} from "@/features/analytics/components/filters/constants";
import type { ChoiceOption } from "@/features/analytics/components/filters/types";
import type { TextNormalize } from "@/features/analytics/components/filters/fields";

// Analytics filter category set for the shared BuildConditions component
// (passed as `config.categories`). Mirrors the CONDITION_CATEGORIES shape so
// BuildConditions/CategoryCard route and group correctly:
//   { id, label, icon, accent, kind: "fields", fields: [<canonical key>...] }
// PLUS a per-field descriptor map (`fieldDescriptors`) the analytics-aware
// CategoryCard body (Phase C, worker-3) reads to render each value control.
//
// Canonical field keys MUST match the backend field registry / shared
// AnalyticsFieldKeySchema exactly (apps/backend/src/modules/analytics/fields.ts).
// Emitted condition value is a SegmentDefinition: { all: [{ field, op, value }] }
// with op in {"in","nin"} and value = the raw ids/codes below.
//
// Explicitly EXCLUDED: device-classification categories (tags / interests /
// demographics = kind:"tags") — those emit a partition-scanning deviceDataPoint
// subquery (plan AC-8b/R8) and are not valid analytics group/filter dims.

// How a field's values are picked and what raw value the condition carries.
//   - "enum":  static {value,label}[] baked in here (gender/kind/source/…)
//   - "dict":  async searched dictionary (platforms/cities/regions) by dictType
//   - "lookup": async id(uuid)->name list (campaign/segment/creative/provider)
//   - "context": options come from the analytics app context at render time
//                (channels, content categories) — the renderer supplies them
//   - "text":   free-form values typed by the user (domains, bundles, ids) —
//                unbounded vocabularies with no list to pick from; `normalize`
//                puts a typed value into the form capture stores
export type AnalyticsFieldControl =
  | {
      kind: "enum";
      options: ChoiceOption<string | number>[];
      valueType: "int" | "bool" | "text";
    }
  | { kind: "dict"; dictType: "platforms" | "cities" | "regions" }
  | { kind: "lookup"; lookup: LookupKind }
  | { kind: "context"; source: "channels" | "categories" }
  | { kind: "text"; normalize?: TextNormalize };

export interface AnalyticsFieldDescriptor {
  key: AnalyticsFieldKey;
  label: string;
  control: AnalyticsFieldControl;
}

export interface AnalyticsCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  accent: string;
  kind: "fields";
  fields: AnalyticsFieldKey[];
  fieldDescriptors: Record<string, AnalyticsFieldDescriptor>;
}

// adRequest.gender is the numeric Gender enum code (0=Unknown/1=Male/2=Female/
// 3=Mixed); segmentFiltersToSql treats gender as an INT field, so the condition
// carries the numeric code. Iterate the enum's numeric members only.
const genderOptions: ChoiceOption<number>[] = Object.values(Gender)
  .filter((v): v is number => typeof v === "number")
  .map((code) => ({ value: code, label: String(Gender[code]) }));

const sourceOptions: ChoiceOption<number>[] = [
  { value: RequestSource.Vast, label: "VAST (OTT)" },
  { value: RequestSource.Rtb, label: "RTB" },
];

const boolOptions: ChoiceOption<string>[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const labelOptions = (labels: Record<number, string>): ChoiceOption<number>[] =>
  Object.entries(labels).map(([code, label]) => ({
    value: Number(code),
    label,
  }));

const descriptor = (
  key: AnalyticsFieldKey,
  label: string,
  control: AnalyticsFieldControl,
): AnalyticsFieldDescriptor => ({ key, label, control });

const makeCategory = (
  id: string,
  label: string,
  icon: LucideIcon,
  accent: string,
  descriptors: AnalyticsFieldDescriptor[],
): AnalyticsCategory => ({
  id,
  label,
  icon,
  accent,
  kind: "fields",
  fields: descriptors.map((d) => d.key),
  fieldDescriptors: Object.fromEntries(descriptors.map((d) => [d.key, d])),
});

// ── request-side (adRequest) categories ─────────────────────────────────────

const locationCategory = (): AnalyticsCategory =>
  makeCategory("location", "Location", MapPin, "blue", [
    descriptor("country", "Country", {
      kind: "enum",
      options: COUNTRY_OPTIONS,
      valueType: "text",
    }),
    descriptor("region", "Region", { kind: "dict", dictType: "regions" }),
    descriptor("city", "City", { kind: "dict", dictType: "cities" }),
  ]);

const platformCategory = (): AnalyticsCategory =>
  makeCategory("platform", "Platform & Source", MonitorSmartphone, "slate", [
    descriptor("platform", "Ad source", {
      kind: "dict",
      dictType: "platforms",
    }),
    descriptor("deviceType", "Device type", {
      kind: "enum",
      options: DEVICE_OPTIONS,
      valueType: "int",
    }),
    descriptor("provider", "Provider / Publisher", {
      kind: "lookup",
      lookup: "providers",
    }),
    descriptor("source", "Request source", {
      kind: "enum",
      options: sourceOptions,
      valueType: "int",
    }),
  ]);

const contentCategory = (): AnalyticsCategory =>
  makeCategory("content", "Content", Clapperboard, "rose", [
    descriptor("channel", "Channel", { kind: "context", source: "channels" }),
    descriptor("categories", "Content categories", {
      kind: "context",
      source: "categories",
    }),
    descriptor("kind", "Ad format", {
      kind: "enum",
      options: KIND_OPTIONS,
      valueType: "int",
    }),
    descriptor("isPaid", "Paid inventory", {
      kind: "enum",
      options: boolOptions,
      valueType: "bool",
    }),
    descriptor("livestream", "Livestream", {
      kind: "enum",
      options: boolOptions,
      valueType: "bool",
    }),
    descriptor("gender", "Co-viewing gender", {
      kind: "enum",
      options: genderOptions,
      valueType: "int",
    }),
  ]);

// Fields captured from the OpenRTB bid request (migration 0033). Labels match
// FIELD_LABELS in ConfigurePanel.
const exchangeCategory = (): AnalyticsCategory =>
  makeCategory("exchange", "Exchange", Network, "amber", [
    descriptor("siteDomain", "Site domain", {
      kind: "text",
      normalize: "host",
    }),
    descriptor("appBundle", "App bundle", { kind: "text" }),
    descriptor("publisherId", "Publisher ID (SSP)", { kind: "text" }),
    descriptor("trafficType", "Traffic type", {
      kind: "enum",
      options: labelOptions(TRAFFIC_TYPE_LABELS),
      valueType: "int",
    }),
    descriptor("adFormat", "Creative format (RTB)", {
      kind: "enum",
      options: labelOptions(AD_FORMAT_LABELS),
      valueType: "int",
    }),
    descriptor("os", "OS", { kind: "text", normalize: "lower" }),
    descriptor("language", "Language", {
      kind: "text",
      normalize: "language",
    }),
    descriptor("dealId", "Deal ID", { kind: "text" }),
  ]);

// ── response-side (adResponse) — Campaign category ──────────────────────────

const campaignCategory = (): AnalyticsCategory =>
  makeCategory("campaign", "Campaign", Megaphone, "violet", [
    descriptor("campaign", "Campaign", { kind: "lookup", lookup: "campaigns" }),
    descriptor("segment", "Segment", { kind: "lookup", lookup: "segments" }),
    descriptor("creative", "Creative", { kind: "lookup", lookup: "creatives" }),
  ]);

/**
 * Build the analytics category list to pass as BuildConditions `config.categories`.
 * The Campaign category (campaign/segment/creative — response-side dims) is
 * included only when `includeCampaign` AND `source === "responses"`.
 */
export const buildAnalyticsCategories = ({
  source,
  includeCampaign,
}: {
  source: AnalyticsSource;
  includeCampaign?: boolean;
}): AnalyticsCategory[] => {
  const categories: AnalyticsCategory[] = [
    locationCategory(),
    platformCategory(),
    exchangeCategory(),
    contentCategory(),
  ];

  if (includeCampaign && source === "responses") {
    categories.push(campaignCategory());
  }

  return categories;
};
